//
//  qaProgramChecklistActivity.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module merges QA checklist notes and completion changes into one timeline.
//

import {
    QaProgramChecklistCompletionState,
    type SourceDocument,
    type TrialQaProgramChecklistItemCompletionStateChange,
    type TrialQaProgramChecklistItemNote
} from "src/api/Tmd.dtos";

/** Describes one note or immutable completion change in a checklist item's activity timeline. */
export type QaProgramChecklistActivity =
    | {
          kind: "note";
          key: string;
          dateEntered?: string;
          author: string;
          note: TrialQaProgramChecklistItemNote;
      }
    | {
          kind: "completion";
          key: string;
          dateEntered?: string;
          author: string;
          change: TrialQaProgramChecklistItemCompletionStateChange;
      };

export interface QaProgramChecklistAttachmentActivity {
    attachment: SourceDocument;
    note: TrialQaProgramChecklistItemNote;
}

const completionStateLabels: Record<QaProgramChecklistCompletionState, string> = {
    [QaProgramChecklistCompletionState.No]: "No",
    [QaProgramChecklistCompletionState.Yes]: "Yes",
    [QaProgramChecklistCompletionState.NotApplicable]: "N/A"
};

/** Returns the user-facing label for a completion state, falling back to Unknown. */
export const getQaProgramChecklistCompletionStateLabel = (
    completionState?: QaProgramChecklistCompletionState
) =>
    completionState === undefined
        ? "Unknown"
        : (completionStateLabels[completionState] ?? "Unknown");

/** Converts an optional service timestamp into a stable ascending sort value. */
const getTimestamp = (dateEntered?: string) => {
    const timestamp = dateEntered ? Date.parse(dateEntered) : Number.NaN;
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

/**
 * Interleaves saved notes and completion changes in chronological order.
 *
 * Postconditions: Inputs are not modified. Equal or missing timestamps retain their original
 * combined order, with notes before completion changes.
 */
export const buildQaProgramChecklistActivity = (
    notes: TrialQaProgramChecklistItemNote[] = [],
    completionStateChanges: TrialQaProgramChecklistItemCompletionStateChange[] = []
): QaProgramChecklistActivity[] =>
    [
        ...notes.map<QaProgramChecklistActivity>((note, index) => ({
            kind: "note",
            key: `note-${note.id ?? index}`,
            dateEntered: note.dateEntered,
            author: note.enteredBy ?? "",
            note
        })),
        ...completionStateChanges.map<QaProgramChecklistActivity>((change, index) => ({
            kind: "completion",
            key: `completion-${change.id ?? index}`,
            dateEntered: change.dateEntered,
            author: change.enteredBy ?? "",
            change
        }))
    ]
        .map((activity, index) => ({ activity, index }))
        .sort(
            (left, right) =>
                getTimestamp(left.activity.dateEntered) -
                    getTimestamp(right.activity.dateEntered) || left.index - right.index
        )
        .map(({ activity }) => activity);

/** Flattens active note attachments, filters filename/message text and orders newest first. */
export const buildQaProgramChecklistAttachments = (
    notes: TrialQaProgramChecklistItemNote[] = [],
    search = ""
): QaProgramChecklistAttachmentActivity[] => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    return notes
        .flatMap(note =>
            (note.attachments ?? [])
                .filter(attachment => attachment.active !== false)
                .map(attachment => ({ attachment, note }))
        )
        .filter(
            ({ attachment, note }) =>
                !normalizedSearch ||
                attachment.name?.toLocaleLowerCase().includes(normalizedSearch) ||
                note.comments?.toLocaleLowerCase().includes(normalizedSearch)
        )
        .sort(
            (left, right) =>
                getTimestamp(right.attachment.dateEntered) -
                    getTimestamp(left.attachment.dateEntered) ||
                (right.attachment.id ?? 0) - (left.attachment.id ?? 0)
        );
};
